import { useContext, useEffect, useRef, useState } from 'react';

import { RenderReadinessContext } from 'contextProviders/RenderReadinessProvider';
import GroupedLoadingContext from 'contexts/GroupedLoadingContext';
import PRINT_LOADING_SELECTOR from 'screens/PrintReportShow/printLoadingSelector';

// Poll interval for the "fully rendered" check.
const POLL_MS = 700;
// Absolute safety net so we always signal rather than hanging to the renderer's
// wait timeout.
const MAX_WAIT_MS = 90000;
// A scheduled report or dashboard's cap: generous, because gadgets on slow
// accounts can take minutes to load, and a render that gives up early retries
// every query from scratch. Must stay below the renderer's READY_TIMEOUT_MS,
// whose attempt must fit in Cloud Run's timeout
// (tools/report-renderer/src/renderReport.ts).
export const REPORT_MAX_WAIT_MS = 300_000;

/**
 * Whether a headless print screen has finished rendering, for the renderer's
 * data-print-ready marker.
 *
 * Ready once the content has loaded and, stable across a few polls:
 * - no gadget has a request in flight, and every expected CSV has registered
 *   (RenderReadinessProvider; a no-op on a screen without one)
 * - nothing in the DOM still shows a spinner or skeleton
 *   (PRINT_LOADING_SELECTOR), covering a gadget still getting ready to make
 *   its request
 * - grid cards have refit to their content
 * then fonts and two paints. A hard cap (maxWaitMs) guarantees it always
 * signals rather than hanging.
 *
 * Changing `resetKey` (e.g. the wallboard slide being shown) starts the check
 * again for the new content.
 */
const usePrintReady = ({
  isContentReady,
  resetKey = '',
  maxWaitMs = MAX_WAIT_MS,
}: {
  isContentReady: boolean;
  resetKey?: string;
  maxWaitMs?: number;
}): boolean => {
  const { isLoading: dataLoading } = useContext(GroupedLoadingContext);
  const { status } = useContext(RenderReadinessContext);
  const [readyKey, setReadyKey] = useState<string | undefined>();
  const isReady = readyKey === resetKey;
  // Read by the poll, which shouldn't restart on every gadget report
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (isReady || !isContentReady) {
      return;
    }
    let cancelled = false;
    let stable = 0;
    const finish = async () => {
      if (document.fonts?.ready) {
        await document.fonts.ready.catch(() => undefined);
      }
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      if (!cancelled) setReadyKey(resetKey);
    };
    const poll = setInterval(() => {
      const stillLoading =
        dataLoading ||
        statusRef.current.loading.length > 0 ||
        statusRef.current.pendingCsvs.length > 0 ||
        document.querySelectorAll(PRINT_LOADING_SELECTOR).length > 0 ||
        // Wait for grid cards to finish refitting to their content (see
        // ReportCanvas buildHeadlessRefitLayout) before capturing.
        document.querySelectorAll('[data-headless-refit-pending="true"]')
          .length > 0;
      if (stillLoading) {
        stable = 0;
        return;
      }
      stable += 1;
      if (stable >= 3) {
        clearInterval(poll);
        finish();
      }
    }, POLL_MS);
    const cap = setTimeout(() => {
      clearInterval(poll);
      if (!cancelled) setReadyKey(resetKey);
    }, maxWaitMs);
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(cap);
    };
  }, [isReady, isContentReady, dataLoading, resetKey, maxWaitMs]);

  return isReady;
};

export default usePrintReady;
